import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

export const detectDeviceTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

let accountTimeZone = detectDeviceTimeZone();
export const getAccountTimeZone = () => accountTimeZone;
export function setAccountTimeZone(zone: string) {
    // Reject invalid values before any screen starts using them.
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    accountTimeZone = zone;
}

type DateValue = Date | string | number;
export function accountDate(value: DateValue): Date {
    if (value instanceof Date) return value;
    // Unmarked server timestamps are UTC; date-only/calendar values remain dates.
    return new Date(typeof value === 'string' && /\d[T ]\d/.test(value) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(value)
        ? `${value}Z` : value);
}

export function accountDateTime(value: DateValue, locales?: Intl.LocalesArgument, options?: Intl.DateTimeFormatOptions) {
    return accountDate(value).toLocaleString(locales, { ...options, timeZone: accountTimeZone });
}
export function accountDateLabel(value: DateValue, locales?: Intl.LocalesArgument, options?: Intl.DateTimeFormatOptions) {
    return accountDate(value).toLocaleDateString(locales, { ...options, timeZone: accountTimeZone });
}
export function accountTimeLabel(value: DateValue, locales?: Intl.LocalesArgument, options?: Intl.DateTimeFormatOptions) {
    return accountDate(value).toLocaleTimeString(locales, { ...options, timeZone: accountTimeZone });
}
export function formatAccountDate(value: DateValue, pattern: string, options?: Parameters<typeof formatInTimeZone>[3]) {
    return formatInTimeZone(accountDate(value), accountTimeZone, pattern, options);
}

/** A date picker's calendar day, interpreted as midnight (or end of day) in the account zone. */
export function accountDayBoundary(calendarDate: Date, end = false): Date {
    const wallTime = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), calendarDate.getDate(),
        end ? 23 : 0, end ? 59 : 0, end ? 59 : 0, end ? 999 : 0);
    return fromZonedTime(wallTime, accountTimeZone);
}

export function availableTimeZones(current?: string): string[] {
    const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
    return [...new Set([detectDeviceTimeZone(), 'UTC', ...(current ? [current] : []),
        ...(intl.supportedValuesOf?.('timeZone') || ['America/Los_Angeles', 'America/Chicago', 'America/New_York', 'Europe/London'])])].sort();
}
